import AiPlatformService from '@/modules/aiContentGenerator/services/aiPlatformService';
import httpService from '@/modules/common/services/httpService';
import fetchAdapter from '@/modules/common/utils/axiosFetchAdapter';
import StreamProcessor from '@/modules/aiContentGenerator/utils/streamProcessor';
import {
  AiErrorFactory,
  AiOperations,
} from '@/modules/aiContentGenerator/utils/aiErrors';
import smartReplyService from '@/modules/bizai/services/smartReplyService';

const aiPlatformService = new AiPlatformService(
  httpService.create(window.pov_config.VUE_APP_API_URL_GATEWAY),
  httpService.create(window.pov_config.VUE_APP_API_URL_GATEWAY, undefined, {
    adapter: fetchAdapter,
  }),
);

const state = {
  textValues: {},
  promptValues: {},
  chatUids: {},
  lastGeneratedMessageUids: {},
  errorValues: {},
  isStreaming: {},
  currentRequestIds: {}, // Track current request per feature to prevent race conditions
  isGenerating: false,
};

const getters = {
  isStreaming: (state) => (feature) => state.isStreaming[feature],
  textValue: (state) => (feature) => state.textValues[feature],
  promptValue: (state) => (feature) => state.promptValues[feature],
  chatUid: (state) => (feature) => state.chatUids[feature],
  errorValue: (state) => (feature) => state.errorValues[feature],
  currentRequestId: (state) => (feature) => state.currentRequestIds[feature],
  isGenerating: (state) => state.isGenerating,
};

const mutations = {
  setIsStreaming(state, { feature, value }) {
    state.isStreaming = { ...state.isStreaming, [feature]: value };
  },
  setTextValue(state, { feature, value }) {
    const cur = state.textValues[feature] ?? '';
    state.textValues = { ...state.textValues, [feature]: cur + value };
  },
  clearTextValue(state, feature) {
    state.textValues[feature] = '';
  },
  setPromptValue(state, { feature, value }) {
    state.promptValues = { ...state.promptValues, [feature]: value };
  },
  setChatUid(state, { feature, value }) {
    state.chatUids = { ...state.chatUids, [feature]: value };
  },
  setErrorValue(state, { feature, value }) {
    state.errorValues = { ...state.errorValues, [feature]: value };
  },
  clearOperationErrors(state, { feature, operationError }) {
    if (AiOperations.is(state.errorValues[feature], operationError)) {
      state.errorValues[feature] = null;
    }
  },
  setLastGeneratedMessageUid(state, { feature, value }) {
    state.lastGeneratedMessageUids = { ...state.lastGeneratedMessageUids, [feature]: value };
  },
  setCurrentRequestId(state, { feature, requestId }) {
    state.currentRequestIds = { ...state.currentRequestIds, [feature]: requestId };
  },
  setIsGenerating(state, isGenerating) {
    state.isGenerating = isGenerating;
  },
};

const actions = {
  async createChat({ commit }, {
    feature, agentName, instruction, windowSize, modelName,
  }) {
    commit('clearOperationErrors', { feature, operationError: AiOperations.createChat });
    try {
      const response = await aiPlatformService.createChat(agentName, { instruction, windowSize, modelName });
      commit('setChatUid', { feature, value: response.data.data.uid });
    } catch (error) {
      commit('setErrorValue', { feature, value: AiErrorFactory(error, AiOperations.createChat) });
    }
  },
  // eslint-disable-next-line no-shadow
  async sendMessage({ commit, getters }, {
    feature, content, streaming = true,
  }) {
    const chatUid = getters.chatUid(feature);
    commit('clearOperationErrors', { feature, operationError: AiOperations.stream });
    try {
      commit('clearTextValue', feature);
      commit('setIsStreaming', { feature, value: true });

      const response = await aiPlatformService.sendMessage(chatUid, content, streaming);
      let gotMessageUid = false;
      await StreamProcessor.processStream(response, (data) => {
        if (!gotMessageUid) {
          gotMessageUid = true;
          commit('setLastGeneratedMessageUid', { feature, value: data.uid });
        }
        if (data.finish_reason !== 'stop') {
          commit('setTextValue', { feature, value: data.delta });
        }
      });
    } catch (error) {
      commit('setErrorValue', { feature, value: AiErrorFactory(error, AiOperations.stream) });
    } finally {
      commit('setIsStreaming', { feature, value: false });
    }
  },
  async getPrompts({ commit, state }, { feature, locale }) {
    commit('clearOperationErrors', { feature, operationError: AiOperations.getPrompts });
    if (state.promptValues[feature]) return;
    try {
      const response = await aiPlatformService.getPrompts(feature, locale);
      commit('setPromptValue', { feature, value: response.data.data });
    } catch (error) {
      commit('setErrorValue', { feature, value: AiErrorFactory(error, AiOperations.getPrompts) });
    }
  },
  // New action for smart reply generation with request tracking
  // eslint-disable-next-line no-shadow
  async generateSmartReply({ commit, getters }, {
    feature,
    matterUid,
    clientUid,
    promptUid,
    populatedText,
    customInstructions,
  }) {
    commit('setIsGenerating', true);
    // Generate unique request ID to track this specific request
    const requestId = `req-${Date.now()}`;

    // Store this as the current request for this feature
    commit('setCurrentRequestId', { feature, requestId });
    commit('clearOperationErrors', { feature, operationError: AiOperations.stream });

    try {
      commit('clearTextValue', feature);
      commit('setIsStreaming', { feature, value: true });

      // Call smart reply service directly (no chat creation, no streaming)
      const response = await smartReplyService.createSmartReply(
        matterUid,
        clientUid,
        promptUid,
        populatedText,
        customInstructions,
      );

      // Check if this is still the current request (user may have clicked another option)
      const currentRequestId = getters.currentRequestId(feature);
      if (currentRequestId !== requestId) {
        return; // Ignore this response, user already made a new request
      }

      // Handle case where service returns undefined or missing response data
      if (!response) {
        commit('setErrorValue', { feature, value: AiErrorFactory(new Error('Empty response from smart reply service'), AiOperations.stream) });
        return;
      }

      // Extract response text from the payload
      const responseText = response.payload?.email_message
                          || response.payload?.sms_message
                          || response.payload?.other_message || '';

      commit('setTextValue', { feature, value: responseText });
      commit('setLastGeneratedMessageUid', { feature, value: response.uid });
    } catch (error) {
      // Check if this is still the current request before showing error
      const currentRequestId = getters.currentRequestId(feature);
      if (currentRequestId === requestId) {
        commit('setErrorValue', { feature, value: AiErrorFactory(error, AiOperations.stream) });
      } else {
        // Silently ignore errors from obsolete requests
      }
    } finally {
      // Only stop loading if this is still the current request
      const currentRequestId = getters.currentRequestId(feature);
      if (currentRequestId === requestId) {
        commit('setIsStreaming', { feature, value: false });
      }
      commit('setIsGenerating', false);
    }
  },
  // Action to cancel ongoing AI generation
  cancelGeneration({ commit }, { feature }) {
    // Generate a new request ID to invalidate the current request
    // This ensures that when the current request completes, it will be ignored
    const cancelRequestId = `cancel-${Date.now()}`;
    commit('setCurrentRequestId', { feature, requestId: cancelRequestId });
    // Stop the loading states
    commit('setIsStreaming', { feature, value: false });
    commit('setIsGenerating', false);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
